/* ==========================================================================
   GLOBAL JS - Maison by Ola
   Core utilities and functionality
   ========================================================================== */

// Debounce utility
function debounce(fn, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      fn(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Throttle utility
function throttle(fn, limit) {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/* ==========================================================================
   Header Functionality
   ========================================================================== */

class Header {
  constructor() {
    this.header = document.querySelector('[data-header]');
    if (!this.header) return;

    this.menuToggle = document.querySelector('[data-menu-toggle]');
    this.mobileNav = document.querySelector('[data-mobile-nav]');
    this.mobileNavOverlay = document.querySelector('[data-mobile-nav-overlay]');
    this.menuClose = document.querySelector('[data-menu-close]');
    this.cartToggle = document.querySelector('[data-cart-toggle]');

    this.isScrolled = false;
    this.lastScrollY = 0;

    this.init();
  }

  init() {
    // Sticky header scroll behavior
    if (this.header.classList.contains('header--sticky') || this.header.classList.contains('header--transparent')) {
      this.handleScroll();
      window.addEventListener('scroll', throttle(() => this.handleScroll(), 100));
    }

    // Mobile menu
    if (this.menuToggle && this.mobileNav) {
      this.menuToggle.addEventListener('click', () => this.openMobileNav());
      this.mobileNavOverlay?.addEventListener('click', () => this.closeMobileNav());
      this.menuClose?.addEventListener('click', () => this.closeMobileNav());

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.mobileNav.classList.contains('is-open')) {
          this.closeMobileNav();
        }
      });
    }

    // Cart drawer toggle
    if (this.cartToggle) {
      this.cartToggle.addEventListener('click', (e) => {
        // Only prevent default if cart drawer exists
        const cartDrawer = document.querySelector('[data-cart-drawer]');
        if (cartDrawer) {
          e.preventDefault();
          this.toggleCartDrawer();
        }
      });
    }
  }

  handleScroll() {
    const scrollY = window.scrollY;
    const threshold = 50;

    if (scrollY > threshold && !this.isScrolled) {
      this.header.classList.add('is-scrolled');
      this.isScrolled = true;
    } else if (scrollY <= threshold && this.isScrolled) {
      this.header.classList.remove('is-scrolled');
      this.isScrolled = false;
    }

    this.lastScrollY = scrollY;
  }

  openMobileNav() {
    this.mobileNav.classList.add('is-open');
    this.mobileNav.setAttribute('aria-hidden', 'false');
    this.menuToggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  closeMobileNav() {
    this.mobileNav.classList.remove('is-open');
    this.mobileNav.setAttribute('aria-hidden', 'true');
    this.menuToggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  toggleCartDrawer() {
    if (window.MaisonByOla?.cartUI) {
      window.MaisonByOla.cartUI.toggleDrawer();
      return;
    }

    const cartDrawer = document.querySelector('[data-cart-drawer]');
    if (cartDrawer) {
      const isOpen = cartDrawer.classList.contains('is-open');
      cartDrawer.classList.toggle('is-open', !isOpen);
      cartDrawer.setAttribute('aria-hidden', isOpen ? 'true' : 'false');
      document.body.style.overflow = isOpen ? '' : 'hidden';
    }
  }
}

/* ==========================================================================
   Cart Functionality
   ========================================================================== */

class Cart {
  constructor() {
    this.cartCountElements = document.querySelectorAll('[data-cart-count]');
    this.init();
  }

  init() {
    // Listen for cart updates
    document.addEventListener('cart:updated', async (e) => {
      if (typeof e.detail?.count === 'number') {
        this.updateCartCount(e.detail.count);
        return;
      }

      try {
        const cart = await Cart.get();
        this.updateCartCount(cart.item_count);
      } catch (error) {
        console.error('Cart count refresh error:', error);
      }
    });
  }

  updateCartCount(count) {
    const nextCount = Number.isFinite(count) ? count : 0;
    this.cartCountElements.forEach(el => {
      el.textContent = nextCount;
      if (nextCount > 0) {
        el.classList.add('has-items');
      } else {
        el.classList.remove('has-items');
      }
    });
  }

  static async add(items, options = {}) {
    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      const data = await response.json();

      if (data.status === 422) {
        throw new Error(data.description || 'Error adding to cart');
      }

      // Get updated cart
      const cart = await Cart.get();
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: {
          cart,
          count: cart.item_count,
          openDrawer: Boolean(options.openDrawer)
        }
      }));

      return data;
    } catch (error) {
      console.error('Cart add error:', error);
      throw error;
    }
  }

  static async get() {
    const response = await fetch('/cart.js', {
      headers: { 'Accept': 'application/json' }
    });
    return response.json();
  }

  static async update(updates) {
    try {
      const response = await fetch(window.routes.cart_update_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      const data = await response.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count: data.item_count, cart: data } }));

      return data;
    } catch (error) {
      console.error('Cart update error:', error);
      throw error;
    }
  }

  static async changeQuantity(key, quantity) {
    try {
      const response = await fetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: key, quantity })
      });

      const data = await response.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count: data.item_count, cart: data } }));

      return data;
    } catch (error) {
      console.error('Cart change error:', error);
      throw error;
    }
  }

  static async updateNote(note) {
    try {
      const response = await fetch(window.routes.cart_update_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ note })
      });

      const data = await response.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count: data.item_count, cart: data } }));

      return data;
    } catch (error) {
      console.error('Cart note error:', error);
      throw error;
    }
  }
}

/* ==========================================================================
   Cart UI
   ========================================================================== */

class CartUI {
  constructor() {
    this.isRefreshingDrawer = false;
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleDocumentChange = this.handleDocumentChange.bind(this);
    this.handleCartUpdated = this.handleCartUpdated.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);

    this.init();
  }

  init() {
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('change', this.handleDocumentChange);
    document.addEventListener('cart:updated', this.handleCartUpdated);
    document.addEventListener('keydown', this.handleKeydown);

    // Hydrate cart upsells on initial page load
    this.hydrateCartUpsells();
  }

  get drawer() {
    return document.querySelector('[data-cart-drawer]');
  }

  isCartPage(element) {
    return Boolean(element?.closest('.cart-page'));
  }

  openDrawer() {
    const drawer = this.drawer;
    if (!drawer) return;

    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  closeDrawer() {
    const drawer = this.drawer;
    if (!drawer) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  toggleDrawer() {
    const drawer = this.drawer;
    if (!drawer) return;

    if (drawer.classList.contains('is-open')) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  async handleDocumentClick(event) {
    const closeTrigger = event.target.closest('[data-cart-drawer-overlay], [data-cart-drawer-close]');
    if (closeTrigger) {
      event.preventDefault();
      this.closeDrawer();
      return;
    }

    const upsellAdd = event.target.closest('[data-upsell-add]');
    if (upsellAdd) {
      event.preventDefault();
      const variantId = upsellAdd.dataset.variantId;
      if (!variantId || upsellAdd.disabled) return;

      upsellAdd.disabled = true;
      try {
        await Cart.add([{ id: variantId, quantity: 1 }], { openDrawer: true });
      } catch (error) {
        console.error('Cart upsell add error:', error);
        upsellAdd.disabled = false;
      }
      return;
    }

    const removeButton = event.target.closest('[data-remove-item]');
    if (!removeButton) return;

    event.preventDefault();

    try {
      await Cart.changeQuantity(removeButton.dataset.key, 0);
      if (this.isCartPage(removeButton)) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Cart remove error:', error);
    }
  }

  async handleDocumentChange(event) {
    const quantityInput = event.target.closest('[data-quantity-input]');
    if (quantityInput) {
      const quantity = parseInt(quantityInput.value || '0', 10);

      try {
        await Cart.changeQuantity(quantityInput.dataset.key, Number.isNaN(quantity) ? 0 : quantity);
        if (this.isCartPage(quantityInput)) {
          window.location.reload();
        }
      } catch (error) {
        console.error('Cart quantity error:', error);
      }

      return;
    }

    const cartNote = event.target.closest('[data-cart-note]');
    if (!cartNote) return;

    try {
      await Cart.updateNote(cartNote.value);
    } catch (error) {
      console.error('Cart note save error:', error);
    }
  }

  async handleCartUpdated(event) {
    const shouldOpenDrawer = Boolean(event.detail?.openDrawer);

    if (!this.drawer) {
      if (shouldOpenDrawer && window.routes?.cart_url) {
        window.location.href = window.routes.cart_url;
      }
      return;
    }

    await this.refreshDrawer(shouldOpenDrawer);
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.drawer?.classList.contains('is-open')) {
      this.closeDrawer();
    }
  }

  async refreshDrawer(shouldOpenDrawer = false) {
    if (this.isRefreshingDrawer) return;

    const currentDrawer = this.drawer;
    if (!currentDrawer) return;

    this.isRefreshingDrawer = true;

    try {
      const sectionUrl = new URL(window.shopUrl || window.location.origin);
      sectionUrl.searchParams.set('section_id', 'cart-drawer');

      const response = await fetch(sectionUrl.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const html = await response.text();
      const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
      const nextDrawer = parsedHtml.querySelector('[data-cart-drawer]');

      if (!nextDrawer) return;

      const shouldRemainOpen = shouldOpenDrawer || currentDrawer.classList.contains('is-open');
      currentDrawer.replaceWith(nextDrawer);

      if (shouldRemainOpen) {
        this.openDrawer();
      } else {
        this.closeDrawer();
      }

      // Re-hydrate upsells against the new anchor product
      this.hydrateCartUpsells();
    } catch (error) {
      console.error('Cart drawer refresh error:', error);
    } finally {
      this.isRefreshingDrawer = false;
    }
  }

  async hydrateCartUpsells() {
    const container = document.querySelector('[data-cart-upsells]');
    if (!container) return;

    const list = container.querySelector('[data-cart-upsells-list]');
    if (!list) return;

    const anchorId = container.dataset.anchorProductId;
    if (!anchorId) {
      container.hidden = true;
      return;
    }

    try {
      const url = new URL('/recommendations/products.json', window.location.origin);
      url.searchParams.set('product_id', anchorId);
      url.searchParams.set('limit', '3');
      url.searchParams.set('intent', 'complementary');

      const response = await fetch(url.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error('Recommendations fetch failed');
      const data = await response.json();
      const products = Array.isArray(data.products) ? data.products : [];

      // Exclude products already in cart
      const existingIds = new Set(
        Array.from(document.querySelectorAll('[data-cart-items] [data-product-id]'))
          .map((el) => String(el.dataset.productId))
      );
      const filtered = products.filter((p) => !existingIds.has(String(p.id))).slice(0, 2);

      if (filtered.length === 0) {
        container.hidden = true;
        return;
      }

      container.hidden = false;
      list.innerHTML = filtered.map((p) => this.renderUpsellCard(p)).join('');
    } catch (error) {
      console.error('Cart upsells hydration error:', error);
      container.hidden = true;
    }
  }

  renderUpsellCard(product) {
    const variantId = product.variants?.[0]?.id;
    if (!variantId) return '';

    const escape = (str) => String(str || '').replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));

    const title = escape(product.title);
    const url = escape(product.url);
    const image = product.featured_image
      ? escape(product.featured_image.replace(/(\.[a-zA-Z]+)(\?|$)/, '_120x120$1$2'))
      : '';
    const priceCents = product.variants?.[0]?.price ?? product.price;
    const priceFormatted = this.formatMoney(priceCents);

    return `
      <div class="cart-upsell">
        ${image ? `<a href="${url}" class="cart-upsell__image" aria-hidden="true" tabindex="-1"><img src="${image}" alt="" width="56" height="56" loading="lazy"></a>` : ''}
        <div class="cart-upsell__info">
          <p class="cart-upsell__title"><a href="${url}" style="color:inherit;text-decoration:none;">${title}</a></p>
          <p class="cart-upsell__price">${priceFormatted}</p>
        </div>
        <button type="button" class="cart-upsell__add" data-upsell-add data-variant-id="${variantId}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add
        </button>
      </div>
    `;
  }

  formatMoney(cents) {
    if (cents == null) return '';
    const amount = Number(cents) / 100;
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AED';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed(2)}`;
    }
  }
}

/* ==========================================================================
   Product Form
   ========================================================================== */

class ProductForm {
  constructor(form) {
    this.form = form;
    if (!this.form) return;

    this.submitButton = this.form.querySelector('[type="submit"]');
    this.variantSelectors = this.form.querySelectorAll('[data-variant-selector]');

    this.init();
  }

  init() {
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));

    // Variant selection
    this.variantSelectors.forEach(selector => {
      selector.addEventListener('change', (e) => this.handleVariantChange(e));
    });
  }

  async handleSubmit(e) {
    e.preventDefault();

    if (this.submitButton.classList.contains('is-loading')) return;

    const formData = new FormData(this.form);
    const variantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity') || 1);

    this.submitButton.classList.add('is-loading');
    this.submitButton.disabled = true;

    try {
      await Cart.add([{ id: variantId, quantity }], { openDrawer: true });

      // Show success state
      this.submitButton.classList.remove('is-loading');
      this.submitButton.classList.add('is-success');

      setTimeout(() => {
        this.submitButton.classList.remove('is-success');
        this.submitButton.disabled = false;
      }, 2000);

    } catch (error) {
      this.submitButton.classList.remove('is-loading');
      this.submitButton.disabled = false;
      alert(error.message || 'Error adding to cart');
    }
  }

  handleVariantChange(e) {
    const variantId = e.target.value;
    // Update hidden variant input
    const variantInput = this.form.querySelector('[name="id"]');
    if (variantInput) {
      variantInput.value = variantId;
    }

    // Dispatch event for other components
    this.form.dispatchEvent(new CustomEvent('variant:changed', {
      detail: { variantId },
      bubbles: true
    }));
  }
}

/* ==========================================================================
   Quantity Selector
   ========================================================================== */

class QuantitySelector {
  constructor(container) {
    this.container = container;
    this.input = container.querySelector('input');
    this.decreaseBtn = container.querySelector('[data-decrease]');
    this.increaseBtn = container.querySelector('[data-increase]');

    if (!this.input) return;

    this.min = parseInt(this.input.getAttribute('min') || 1);
    this.max = parseInt(this.input.getAttribute('max') || 99);

    this.init();
  }

  init() {
    this.decreaseBtn?.addEventListener('click', () => this.decrease());
    this.increaseBtn?.addEventListener('click', () => this.increase());
    this.input.addEventListener('change', () => this.validate());
  }

  decrease() {
    const currentValue = parseInt(this.input.value);
    if (currentValue > this.min) {
      this.input.value = currentValue - 1;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  increase() {
    const currentValue = parseInt(this.input.value);
    if (currentValue < this.max) {
      this.input.value = currentValue + 1;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  validate() {
    let value = parseInt(this.input.value);
    if (isNaN(value) || value < this.min) {
      value = this.min;
    } else if (value > this.max) {
      value = this.max;
    }
    this.input.value = value;
  }
}

/* ==========================================================================
   Initialize on DOM Ready
   ========================================================================== */

document.addEventListener('DOMContentLoaded', () => {
  // Initialize header
  new Header();

  // Initialize cart
  new Cart();
  const cartUI = new CartUI();

  // Initialize product forms
  document.querySelectorAll('[data-product-form]').forEach(form => {
    new ProductForm(form);
  });

  // Initialize quantity selectors
  document.querySelectorAll('[data-quantity-selector]').forEach(container => {
    new QuantitySelector(container);
  });

  window.MaisonByOla.cartUI = cartUI;
});

// Export for use in other scripts
window.MaisonByOla = {
  Cart,
  CartUI,
  ProductForm,
  QuantitySelector,
  debounce,
  throttle
};
